import { useEffect, useRef } from "react";

/**
 * Living 3D particle sphere with the "brochure" design — a dense, bright
 * blue/white point-cloud BALL where particles on the surface AND inside the
 * volume drift and swirl, with a luminous core. Pure canvas, no deps.
 *
 * Props:
 *  - size: rendered size in CSS px (square canvas)
 *  - color: base particle color [r,g,b]
 */
export const BrochureOrb = ({ speaking = false, level = null, size = 248, color = [120, 160, 255], theme = "dark", className = "" }) => {
  const canvasRef = useRef(null);
  const stateRef = useRef({ speaking, level });
  stateRef.current.speaking = speaking;
  stateRef.current.level = level;
  const light = theme === "light";

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssSize = size;
    canvas.width = cssSize * dpr;
    canvas.height = cssSize * dpr;
    ctx.scale(dpr, dpr);

    const cx = cssSize / 2;
    const cy = cssSize / 2;
    const baseR = cssSize * 0.34;
    const [cr, cg, cb] = color;

    // Fibonacci directions for an even angular spread, then assign each particle
    // a radius factor: ~55% sit on the shell, ~45% fill the inner volume so the
    // ball looks solid and the inner particles move too.
    const N = 2800;
    const golden = Math.PI * (3 - Math.sqrt(5));
    const pts = [];
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const theta = golden * i;
      const dir = { x: Math.cos(theta) * r, y, z: Math.sin(theta) * r };
      // radius factor: shell vs volume (cbrt → uniform volume fill)
      const rf = Math.random() < 0.55 ? 0.92 + Math.random() * 0.08 : Math.cbrt(Math.random());
      pts.push({ ...dir, rf, seed: Math.random() * 1000 });
    }

    const tiltX = 0.42; // fixed axis tilt so it reads as a ball, not a flat coin
    const cosT = Math.cos(tiltX), sinT = Math.sin(tiltX);

    let raf, t = 0, amp = 0, last = 0;
    const render = (ts) => {
      // frame-rate independent: advance time by real elapsed frames vs a 60fps baseline
      const dt = last ? Math.min((ts - last) / 16.667, 3) : 1;
      last = ts;
      t += 0.014 * dt;
      const { speaking: spk, level: lvl } = stateRef.current;
      // SAME energy envelope as VoiceOrb: pulses sharply with the words / live audio.
      let target;
      if (lvl != null) {
        target = Math.max(0.1, Math.min(1, lvl * 1.5));
      } else if (spk) {
        const syl =
          (0.5 + 0.5 * Math.sin(t * 11.0)) *
          (0.5 + 0.5 * Math.sin(t * 6.7 + 1.7)) *
          (0.55 + 0.45 * Math.sin(t * 3.1 + 0.5));
        target = 0.14 + 0.86 * Math.pow(syl, 0.55);
      } else {
        target = 0.12 + 0.05 * Math.sin(t * 1.1); // idle breathing
      }
      // fast attack, quicker release — identical feel to VoiceOrb (dt-normalized)
      const k = target > amp ? 0.5 : 0.26;
      amp += (target - amp) * (1 - Math.pow(1 - k, dt));

      ctx.clearRect(0, 0, cssSize, cssSize);

      // luminous core + halo — fades to transparent at the edges so the orb
      // blends seamlessly into whatever background sits behind the canvas.
      const glowR = baseR * 1.9;
      const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
      if (light) {
        bg.addColorStop(0, `rgba(${cr},${cg},${cb},${0.14 + amp * 0.08})`);
        bg.addColorStop(0.3, `rgba(${cr},${cg},${cb},${0.07 + amp * 0.04})`);
        bg.addColorStop(0.6, `rgba(${cr},${cg},${cb},0.02)`);
        bg.addColorStop(1, "rgba(255,255,255,0)");
      } else {
        bg.addColorStop(0, `rgba(220,235,255,${0.22 + amp * 0.12})`);
        bg.addColorStop(0.18, `rgba(${cr},${cg},${cb},${0.16 + amp * 0.08})`);
        bg.addColorStop(0.5, `rgba(${cr},${cg},${cb},0.04)`);
        bg.addColorStop(1, "rgba(0,0,0,0)");
      }
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, cssSize, cssSize);

      const rotY = t * 0.5;
      const cosY = Math.cos(rotY), sinY = Math.sin(rotY);

      ctx.globalCompositeOperation = light ? "source-over" : "lighter";
      for (let i = 0; i < N; i++) {
        const p = pts[i];
        // swirl: per-particle organic displacement so inner particles move
        const n =
          Math.sin(p.seed + t * 1.7 + p.y * 3.0) * 0.5 +
          Math.sin(p.seed * 0.7 + t * 2.4 + p.x * 2.2) * 0.3 +
          Math.cos(p.seed * 1.3 + t * 1.2 + p.z * 2.6) * 0.2;
        // absolute voice-driven displacement (NOT scaled by rf) so every particle —
        // shell AND inner — visibly pulses with the voice, exactly like VoiceOrb,
        // plus a global breathing scale that grows the whole ball when speaking.
        const rr = baseR * (p.rf + n * (0.06 + amp * 0.42)) * (1 + amp * 0.10);

        // rotate around Y
        let x = p.x * cosY - p.z * sinY;
        let z = p.x * sinY + p.z * cosY;
        let y = p.y;
        // tilt around X (axis spin look)
        const y2 = y * cosT - z * sinT;
        const z2 = y * sinT + z * cosT;
        y = y2; z = z2;

        const sx = cx + x * rr;
        const sy = cy + y * rr;
        const depth = (z + 1) / 2; // 0 back .. 1 front

        // inner particles glow brighter/whiter to build a luminous core
        const coreness = 1 - p.rf; // 0 shell .. 1 center
        if (light) {
          // dark brand particles on white — front more opaque, edges fade into white
          const dr = (cr * 0.62) | 0, dg = (cg * 0.62) | 0, db = (cb * 0.62) | 0;
          const alpha = (0.1 + depth * 0.62) * (0.5 + amp * 0.6) * (0.72 + coreness * 0.42);
          ctx.fillStyle = `rgba(${dr},${dg},${db},${Math.min(0.92, alpha)})`;
          const rad = 0.5 + depth * 1.3 + coreness * 0.5;
          ctx.beginPath();
          ctx.arc(sx, sy, rad, 0, Math.PI * 2);
          ctx.fill();
        } else {
          const w = depth * depth * 110 + coreness * 90;
          const alpha = (0.18 + depth * 0.7) * (0.45 + amp * 0.65) * (0.7 + coreness * 0.5);
          ctx.fillStyle = `rgba(${Math.min(255, cr + w)},${Math.min(255, cg + w)},${Math.min(255, cb + w)},${Math.min(1, alpha)})`;
          const rad = 0.5 + depth * 1.3 + coreness * 0.5;
          ctx.beginPath();
          ctx.arc(sx, sy, rad, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalCompositeOperation = "source-over";
      raf = requestAnimationFrame(render);
    };
    render();
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, color[0], color[1], color[2], theme]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="brochure-orb"
      style={{ width: size, height: size, display: "block" }}
      className={className}
      aria-hidden="true"
    />
  );
};

export default BrochureOrb;
